import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, retryableSupabase } from '../lib/supabase';
import { setUserRole } from '../services/analyticsService';
import { errorMonitoring } from '../services/errorMonitoring';


const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [retryState, setRetryState] = useState(null)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Use retryable client for profile loading
        const result = await retryableSupabase?.retrySelect(
          'user_profiles',
          '*',
          { id: userId }
        );
        
        if (result?.data && result?.data?.length > 0) {
          const profile = result?.data?.[0];
          setUserProfile(profile);
          // Track user role in GA4
          if (profile?.role) {
            setUserRole(profile?.role);
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
        
        // Check if circuit breaker is open
        if (error?.circuitOpen) {
          setRetryState({
            type: 'circuit_open',
            message: 'Service temporarily unavailable. Please try again later.',
          });
        }
        
        errorMonitoring?.handleQueryError(
          error,
          { table: 'user_profiles', operation: 'SELECT', query: 'Load user profile' },
          { userId },
          false
        );
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      setRetryState(null)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      // Use retryable client for authentication
      const { data, error } = await retryableSupabase?.retryAuth(
        () => supabase?.auth?.signInWithPassword({ email, password }),
        'signIn'
      );
      
      if (error) {
        // Log authentication error
        errorMonitoring?.handleAuthError(
          error,
          { action: 'signIn', email },
          false
        );
      }
      
      if (data?.user && !error) {
        // Check if user is suspended
        const { data: isSuspended, error: suspensionError } = await supabase?.rpc('is_user_suspended', {
          p_user_id: data?.user?.id
        });

        if (suspensionError) {
          console.error('Error checking suspension status:', suspensionError);
        }

        if (isSuspended) {
          // Get suspension details
          const { data: suspensionDetails, error: detailsError } = await supabase?.rpc('get_active_suspension', {
            p_user_id: data?.user?.id
          });

          // Sign out the suspended user
          await supabase?.auth?.signOut();

          const suspension = suspensionDetails?.[0];
          let message = 'Your account has been suspended. ';
          if (suspension?.is_permanent) {
            message += 'This suspension is permanent.';
          } else if (suspension?.expires_at) {
            const expiryDate = new Date(suspension?.expires_at)?.toLocaleDateString();
            message += `Suspension expires on ${expiryDate}.`;
          }
          if (suspension?.reason) {
            message += ` Reason: ${suspension?.reason}`;
          }

          return { 
            data: null, 
            error: { message } 
          };
        }

        // Track sign-in event
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'login', {
            method: 'email',
          })
        }
      }
      return { data, error }
    } catch (error) {
      // Log network error
      errorMonitoring?.handleNetworkError(
        error,
        { action: 'signIn', email },
        false
      );
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      if (error) {
        // Log authentication error
        errorMonitoring?.handleAuthError(
          error,
          { action: 'signUp', email },
          false
        );
      }
      
      if (data?.user && !error) {
        // Track sign-up event
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'sign_up', {
            method: 'email',
          })
        }
      }
      return { data, error }
    } catch (error) {
      // Log network error
      errorMonitoring?.handleNetworkError(
        error,
        { action: 'signUp', email },
        false
      );
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      
      if (error) {
        // Log authentication error
        errorMonitoring?.handleAuthError(
          error,
          { action: 'signOut' },
          false
        );
      }
      
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      // Log network error
      errorMonitoring?.handleNetworkError(
        error,
        { action: 'signOut' },
        false
      );
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email)
      
      if (error) {
        // Log authentication error
        errorMonitoring?.handleAuthError(
          error,
          { action: 'resetPassword', email },
          false
        );
      }
      
      return { data, error }
    } catch (error) {
      // Log network error
      errorMonitoring?.handleNetworkError(
        error,
        { action: 'resetPassword', email },
        false
      );
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      
      if (error) {
        // Log query error
        errorMonitoring?.handleQueryError(
          error,
          { table: 'user_profiles', operation: 'UPDATE', query: 'Update user profile' },
          { userId: user?.id },
          false
        );
      }
      
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      // Log network error
      errorMonitoring?.handleNetworkError(
        error,
        { action: 'updateProfile', userId: user?.id },
        false
      );
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
